import { useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";

import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";

import { TemplatePageType, kToolbarHeight } from "models";
import {
  useAppSelector,
  useAppDispatch,
  loadTemplate,
  setLoading,
  closeSettingsPage,
  closeCoverPicker,
  closeWelcome,
} from "utils/store";
import { LoadingController } from "utils/ui";
import { getTemplate } from "utils/api";

import DefectListTemplate from "./components/defect-list-template/defect-list-template";
import ContentsTemplate from "./components/contents-template/contents-template";
import { CustomPageTemplate } from "pages/CustomPageTemplate";
import { TemplateToolbar } from "./components/template-toolbar";
import { GeneralSettings } from "./components/general-settings";
import { PageNavigation } from "./components/page-navigation";
import { CoverDesignPicker } from "./components/cover-design-picker";
import { WelcomeModal } from "./components/welcome-modal";

// https://atlassian.design/components/pragmatic-drag-and-drop/core-package
// https://atlassian.design/components/pragmatic-drag-and-drop/examples/#simple-list-on-other-stacks
// Working example https://github.com/alexreardon/pdnd-react-tailwind/blob/main/src/list.tsx

export function TemplateBuilder() {
  const dispatch = useAppDispatch();
  const showSettings = useAppSelector((state) => state.templateBuilderSlice.showSettings);
  const showCoverPicker = useAppSelector((state) => state.templateBuilderSlice.showCoverPicker);
  const showWelcome = useAppSelector((state) => state.templateBuilderSlice.showWelcome);
  const loading = useAppSelector((state) => state.templateBuilderSlice.loading);
  const selectedPageIndex = useAppSelector((state) => state.templateBuilderSlice.selectedPageIndex);
  const pageType = useAppSelector((state) => state.templateBuilderSlice.pages[selectedPageIndex]?.type, shallowEqual);
  const pageId = useAppSelector((state) => state.templateBuilderSlice.pages[selectedPageIndex]?.id, shallowEqual);

  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    async function loadData() {
      const result = await getTemplate();
      if (result) {
        dispatch(loadTemplate(result));
      }
      setTimeout(() => {
        dispatch(setLoading(false));
      }, 1000);
    }

    loadData();
  }, []);

  return (
    <div className="overflow-hidden bg-[var(--surface-ground)]" style={{ height: "100vh", width: "100vw" }}>
      <TemplateToolbar />

      <div className="flex flex-row" style={{ height: `calc(100vh - ${kToolbarHeight}px)` }}>
        <PageNavigation />

        {/* <!-- COLUMN TWO --> */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          {pageType === TemplatePageType.coverPage && <CustomPageTemplate key={pageId} />}
          {pageType === TemplatePageType.contentsPage && <ContentsTemplate />}
          {pageType === TemplatePageType.defectList && <DefectListTemplate />}
          {pageType === TemplatePageType.customPage && <CustomPageTemplate key={pageId} />}
          {pageType === undefined && (
            <div className="p-4">
              <h1>No Page Selected</h1>
            </div>
          )}
        </div>
      </div>

      <Dialog
        header="Header & Footer"
        visible={showSettings}
        onHide={() => dispatch(closeSettingsPage())}
        style={{ width: "min(760px, 94vw)" }}
        dismissableMask
      >
        <GeneralSettings />
      </Dialog>

      <WelcomeModal visible={showWelcome} onHide={() => dispatch(closeWelcome())} />

      <CoverDesignPicker visible={showCoverPicker && !showWelcome} onHide={() => dispatch(closeCoverPicker())} />

      <LoadingController loading={loading} />

      <ConfirmDialog />
    </div>
  );
}
